import React from 'react';
import classnames from 'classnames';
import { groupBy } from 'lodash';
import GlobalProps from '../globalProps';
import { numerify } from '../../util/util';
import { Seatmap } from '../../model/seatmap/seatmap';
import { Section, Seat, SeatmapMode } from '../../model/seatmap/types';
import { topLeftOf } from '../../model/seatmap/helper';

interface Props extends GlobalProps {
  seatmap: Seatmap;
  section: Section;
  mode: SeatmapMode;
}

export class SeatmapSection extends React.Component<Props, {}> {
  private lastRenderKey = '';

  public shouldComponentUpdate(props: Props): boolean {
    return this.lastRenderKey !== this.renderKey(props.section, props.seatmap.selection);
  }

  public render(): JSX.Element {
    const { seatmap, section, style, className, mode } = this.props;
    const selected = seatmap.selection.section === section;
    this.lastRenderKey = this.renderKey(section, seatmap.selection);
    const labelAnchor = topLeftOf(section.hullPoints);
    const rows = Object.values(groupBy(section.seats, 'row'));

    const stateClass = this.determineStateClass(section);

    return (
      <g
        style={style}
        className={classnames(className)}
        onMouseEnter={() => {
          if (section.generalAdmission) {
            seatmap.selection.hoveredGeneralAdmissionSection = section;
          }
        }}
        onMouseLeave={() => {
          seatmap.selection.hoveredGeneralAdmissionSection = null;
        }}
      >
        <polygon
          className={classnames('seatmap__group', stateClass, {
            'seatmap__group--selected': selected,
          })}
          points={section.hullPoints.map((p) => `${p.join(',')}`).join(' ')}
          onClick={() => {
            if (section.generalAdmission) {
              seatmap.selection.toggleSection(section);
            } else {
              seatmap.selection.toggleSeatsInSection(section);
            }
          }}
          style={mode === 'pricing-category' ? { fill: section.pricingCategory?.color } : undefined}
        />
        {labelAnchor && (
          <text className="seatmap__group-name" textAnchor="left" x={labelAnchor[0] + 2} y={labelAnchor[1] + 8}>
            {section.name}
          </text>
        )}

        {
          <>
            {section.seats.map((seat) => {
              return (
                <React.Fragment key={seat.id}>
                  <circle
                    id={seat.id}
                    key={seat.id}
                    className={this.seatClasses(seat)}
                    cx={seat.x}
                    cy={seat.y}
                    r="4"
                    onClick={(e) => {
                      if (e.shiftKey) {
                        seatmap.selection.toggleSeatsInbetweenLastInteractionAnd(seat);
                      } else {
                        seatmap.selection.toggleSeat(seat);
                      }
                    }}
                    onMouseEnter={() => (seatmap.selection.hoveredSeat = seat)}
                    onMouseLeave={() => (seatmap.selection.hoveredSeat = null)}
                    style={mode === 'pricing-category' ? { fill: seat.pricingCategory?.color } : undefined}
                  />
                  {seat.allocation?.permanent && (
                    <text className="seatmap__seat--perm-block" x={seat.x} y={seat.y}>
                      x
                    </text>
                  )}
                </React.Fragment>
              );
            })}
          </>
        }
        {rows.map((row) => {
          row.sort((a, b) => numerify(a.name) - numerify(b.name));
          const firstSeat = row[0];
          const secondSeat = row[1];

          if (firstSeat && secondSeat) {
            const label = {
              x: secondSeat.x - firstSeat.x,
              y: secondSeat.y - firstSeat.y - 3,
            };
            return (
              <text
                className="seatmap__label"
                key={`label-${firstSeat.id}`}
                textAnchor="middle"
                x={firstSeat.x - label.x}
                y={firstSeat.y - label.y}
              >
                {firstSeat.row}
              </text>
            );
          }
          return null;
        })}
      </g>
    );
  }

  private seatClasses(seat: Seat): string {
    const selection = this.props.seatmap.selection;
    const selected = selection.seats.has(seat);
    const status = seat.allocation?.status ?? 'unknown';
    return classnames('seatmap__seat', `seatmap__seat--${status}`, {
      'seatmap__seat--selected': selected,
      'seatmap__perm-blocked': seat.allocation?.permanent,
    });
  }

  private determineStateClass(group: Section): string {
    if (!group.generalAdmission) return 'seatmap__group--seated';
    const allocation = group.allocation;
    if (!allocation) return 'seatmap__group--general-admission-unknown';
    if (allocation.capacity === 0) return 'seatmap__group--general-admission-no-contingent';
    if (allocation.blocked === allocation.capacity) return 'seatmap__group--general-admission-all-blocked';
    if (allocation.available === 0) return 'seatmap__group--general-admission-all-sold';
    return 'seatmap__group--general-admission-available';
  }

  private renderKey(section: Section, selection: Seatmap['selection']): string {
    const components: string[] = [];
    components.push(section.hash);
    components.push(selection.section === section ? 'section' : '');
    section.seats.filter((seat) => selection.seats.has(seat)).forEach((seat) => components.push(seat.id));
    return components.join('-');
  }
}
